import type { FairPrice, FeeSchedule, Token, Valuation } from "../api/types";
import { useNow } from "../hooks/useNow";
import { useBooks } from "../store/useBooks";
import { formatAge } from "../lib/format";
import { Field, FieldRow, Qual, Val, W } from "./Field";
import {
  bookMid,
  deviationArrow,
  deviationTitle,
  deviationTone,
  edgeLabel,
  edgeTone,
  fairByIndex,
  formatDeviationMagnitude,
  formatDeviationPct,
  formatFair,
  formatIndex,
  formatSigma,
  hasDeviation,
  hasStrikeLag,
  indexAge,
  metricLabel,
  showsIndexAge,
  sourceTitle,
  strikeLagLabel,
  strikeTitle,
  takerBand,
  takerEdgeLabel,
  unknownReason,
} from "../lib/valuation";
import {
  isStrikeForecast,
  strikeForecastTitle,
  strikeSigmaLabel,
  strikeTag,
  strikeTagTitle,
} from "../lib/preopen";

/**
 * Страйк, индекс, отклонение и оценка исхода по модели — ОДНОЙ строкой.
 *
 * Средний ярус карточки рынка (components/MarketOverview.tsx): над ним окно и
 * отсчёт, под ним сверка того же индекса. Своей карточки у строки нет и не было:
 * шапку рынка специально ужимали с трёх рядов до одного, потому что на
 * пятиминутном окне цена решения — это прокрутка до панели ордеров.
 *
 * Что видно и почему:
 *
 *   - страйк и индекс рядом — их сравнивают глазами, и это единственная пара
 *     чисел, ради которой строка заведена;
 *   - отклонение цветом: знак читается раньше цифр;
 *   - справедливые цены обоих исходов — в порядке outcome_index, тем же, в
 *     каком стоят лесенки ниже;
 *   - σ и метрика источника приглушённым текстом: они объясняют оценку, но
 *     решения по ним не принимают;
 *   - возраст значения — только когда оно СТАРОЕ. Свежая метка «1 с» на каждом
 *     обновлении приучает не читать это место вовсе.
 *
 * Оценка приходит СВЕРХУ, одним запросом на всю карточку: строка и сверка под
 * ней описывают одно и то же число, и разъехаться по возрасту они не вправе.
 *
 * Логика вся в lib/valuation.ts: компонентных тестов в проекте нет, поэтому в
 * .tsx остаётся разметка. Расстояния и слоты — не здесь, а в components/Field.tsx:
 * ярусы карточки обязаны набираться одинаково, иначе столбик K / S / Ŝ разъедется
 * на первой же правке одного из них.
 */
export function IndexRow({
  v,
  fees,
  tokens,
  note,
}: {
  v: Valuation | undefined;
  /**
   * Расписание комиссии рынка. Нужно порогу решения: справедливая цена без него
   * предлагает сделку, которая с комиссией уже убыточна (см. takerBand).
   */
  fees: FeeSchedule | undefined;
  /**
   * Токены рынка — чтобы найти стакан каждого исхода.
   *
   * Нужны затем, что цена модели сама по себе решения не даёт: торгуют РАЗНИЦУ
   * между ней и тем, что просит рынок. Без токенов разница не считается, и
   * строка честно молчит вместо того, чтобы показать преимущество, которого
   * никто не измерял.
   */
  tokens: readonly Token[] | undefined;
  /** Почему оценки нет — словами. Пустота на месте чисел читалась бы как ноль. */
  note: string;
}) {
  // Часы тикают ЗДЕСЬ, в листе дерева: правило hooks/useNow.ts. Возраст цены
  // обязан расти сам, иначе замерший поток выглядит живым до следующего ответа.
  const nowMs = useNow(1000);

  if (!v) {
    return (
      <Row>
        <span className="text-[var(--color-muted)]">{note}</span>
      </Row>
    );
  }

  const age = indexAge(v, nowMs);
  // Правило — в lib/valuation.ts: у закончившегося окна индекс это его ИТОГ, он
  // обязан быть старым, и общий порог зажигал на нём тревогу о поломке.
  const showAge = showsIndexAge(v, nowMs);
  const flat = parseFloat(v.deviation ?? "0") === 0;

  return (
    <Row>
      {/* K и S вместо слов «страйк» и «индекс».
          Обозначения те же, что в формуле модели, и они короче слова на четыре
          знака каждое — а строка ЛИПКАЯ, место в ней оплачивается высотой
          экрана постоянно. Слово остаётся в подсказке: читают его один раз,
          когда разбираются, что тут вообще показано.

          Оба — подписи в один знак (`tag`): моноширинный слот ставит их числа в
          столбик с нашим отчётом ярусом ниже, а сравнивают эти три величины
          глазом по вертикали. */}
      {/* До открытия окна на месте страйка стоит его ПРОГНОЗ — с крышкой над
          буквой, как Ŝ ярусом ниже, и с разбросом рядом. Без крышки число,
          которое ещё двигается, читалось бы как зафиксированный страйк. Разброс
          — по той же причине, что покрытие окна в подсказке: «K̂ ±40» и
          «K̂ ±2» на экране иначе неразличимы. */}
      <Field
        tag
        label={strikeTag(v)}
        labelTitle={strikeTagTitle(v)}
        title={isStrikeForecast(v) ? strikeForecastTitle(v) : strikeTitle(v)}
      >
        {/* Без приглушения: `dim` в карточке означает «значение устарело», а
            прогноз свежий — его отличает подпись, а не тон. */}
        <Val w={W.price}>{formatIndex(v.strike)}</Val>
        {strikeSigmaLabel(v) !== "" && <Qual w={W.delta}>{strikeSigmaLabel(v)}</Qual>}
        {/*
          Опоздание страйка — рядом с ним, а не в подсказке, и только когда оно
          есть. Источник изредка пропускает секунду начала окна; взятое тогда
          значение выглядит нормальным, а вся оценка окна смещена в одну сторону.
          Молчать об этом нельзя — именно так расхождение со Polymarket и
          выглядит косметическим. Запаса ширины под него нет намеренно: случай
          редкий, а дыра в строке ждала бы его каждое окно.
        */}
        {hasStrikeLag(v) && <span className="text-amber-300">{strikeLagLabel(v)}</span>}
      </Field>

      <Field
        tag
        label="S"
        labelTitle="индекс — текущее значение метрики, по которой рынок резолвится"
      >
        <Val w={W.price} dim={showAge}>
          {formatIndex(v.index)}
        </Val>
        {showAge && (
          <span className="num text-amber-300" title="значение индекса не обновлялось">
            {Number.isFinite(age) ? formatAge(age) : "нет данных"}
          </span>
        )}
      </Field>

      {/*
        Отклонение показывается ОТДЕЛЬНО от оценки модели, а не вместе с ней.
        Считается оно из страйка и индекса, а модели нужна ещё и волатильность —
        и пока σ набирается (первые минуты после перезапуска трейдера), «выше или
        ниже и насколько» известно точно. Спрятать это вместе с моделью значило
        бы убрать с экрана ровно то, ради чего строка заведена.
      */}
      {hasDeviation(v) && (
        <Field w={W.dev} title={deviationTitle(v.deviation)}>
          {/* Стрелка вместо «выше на»/«ниже на»: направление читается раньше
              цифр и тем же знаком, каким его читают в стакане. Слова остаются
              в подсказке — на них уходило одиннадцать знаков липкой строки. */}
          <Val color={toneColor(deviationTone(v.deviation))}>
            {deviationArrow(v.deviation)}
            {!flat && ` ${formatDeviationMagnitude(v.deviation, v.index)}`}
          </Val>
          {!flat && (
            <Qual color={toneColor(deviationTone(v.deviation))}>
              ({formatDeviationPct(v.deviation_pct)})
            </Qual>
          )}
        </Field>
      )}

      {v.known && (
        <>
          {/* «модель» — подпись к ОБОИМ исходам, поэтому они и стоят одним
              полем: разлучи их зазором поля, и подпись стала бы относиться к
              первому. Внутри исходы разведены шагом — числа разные, величина
              одна.

              Запаса ширины у поля нет: цифры внутри держат свои слоты сами, а
              преимущество и полоса пропадают не посекундно, а вместе со
              стаканом — то есть надолго, и дыра в полстроки ждала бы их всё
              это время. */}
          {/* Подпись едет ВМЕСТЕ с числами. У закончившегося окна цен модели нет
              и быть не может — предсказывать уже нечего, — а «модель» без них
              висела бы пустой подписью посреди строки. σ рядом остаётся: она
              свойство актива, а не прогноз. */}
          {fairByIndex(v).length > 0 && (
          <Field label="модель">
            {fairByIndex(v).map((f, i) => (
              <ModelPrice
                key={f.outcome_index}
                fair={f}
                fees={fees}
                assetId={tokens?.find((t) => t.outcome_index === f.outcome_index)?.asset_id}
                step={i > 0}
              />
            ))}
          </Field>
          )}

          <Field
            label="σ"
            title={`волатильность базового актива, годовая — оценена по ${v.sigma_samples} значениям потока`}
          >
            <Val w={W.sigma}>{formatSigma(v.sigma_annual)}</Val>
          </Field>
        </>
      )}

      {!v.known && (
        // Словами, а не пустым местом: пустота на месте оценки читается как
        // «модель согласна с рынком».
        <span className="text-amber-300">{unknownReason(v)}</span>
      )}

      <span className="ml-auto text-[var(--color-muted)]" title={sourceTitle(v)}>
        {metricLabel(v)}
      </span>
    </Row>
  );
}

/** Оболочка ряда — тот же отступ и та же разделительная линия, что у шапки. */
function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b border-[var(--color-border)] px-4 py-1 text-xs">
      <FieldRow>{children}</FieldRow>
    </div>
  );
}

/** Токены темы, а не свои значения: палитра терминала одна (lib/tier.ts). */
function toneColor(tone: "up" | "down" | "flat"): string {
  if (tone === "up") return "var(--color-buy)";
  if (tone === "down") return "var(--color-sell)";
  return "var(--color-muted)";
}

/**
 * Цена исхода по модели, её преимущество над стаканом и полоса безубытка.
 *
 * ОТДЕЛЬНЫЙ компонент на исход, а не кусок разметки в цикле, и причина
 * техническая: середину стакана каждый исход берёт из СВОЕГО стакана, а хук в
 * цикле по исходам не поставишь. Заодно перерисовка от дельт одного стакана не
 * трогает второй.
 *
 * Подписка узкая — селектор отдаёт СТРОКУ, а не стакан: стакан меняется
 * несколько раз в секунду, а середина до тика цены — редко, и широкая подписка
 * перерисовывала бы липкую строку на каждой дельте.
 */
function ModelPrice({
  fair,
  fees,
  assetId,
  step,
}: {
  fair: FairPrice;
  fees: FeeSchedule | undefined;
  assetId: string | undefined;
  /** Второй исход в поле модели: отделяется шагом, а не зазором подписи. */
  step: boolean;
}) {
  // Два узких селектора, а не один на пару: каждый отдаёт СТРОКУ, и сравнение
  // по ссылке работает само. Пара вернула бы новый массив на каждой дельте
  // стакана и перерисовывала бы липкую строку постоянно.
  const mid = useBooks((s) => {
    const b = assetId ? s.books[assetId] : undefined;
    return bookMid(b?.bids[0]?.price, b?.asks[0]?.price);
  });
  const ask = useBooks((s) => {
    const b = assetId ? s.books[assetId] : undefined;
    return b?.asks[0]?.price ?? null;
  });

  // Полоса безубытка показывается У КАЖДОГО исхода, а не одна на строку:
  // комиссия зависит от цены, а цены исходов дополняют друг друга до единицы и
  // совпадают только у 0.5.
  const band = takerBand(fair.price, fees);
  const edge = edgeLabel(fair.price, mid);
  const exec = takerEdgeLabel(fair.price, ask, fees);

  return (
    <>
      {/* Исход цветом: Up и Down различают до чтения цифр, и цвета те же, что
          у стаканов ниже. Единственная подпись карточки, которая не серая, — и
          не по недосмотру: это не имя величины, а сам исход. */}
      <span
        className={step ? "field-step num font-medium" : "num font-medium"}
        style={{ color: outcomeColor(fair.outcome) }}
      >
        {fair.outcome}
      </span>
      <Val w={W.fair}>{formatFair(fair.price)}</Val>
      {/* Преимущество над серединой стакана — в скобках и СРАЗУ за ценой:
          модель отвечает «сколько исход стоит», а торгуют разницу между этим и
          тем, что просит рынок. Пусто, когда стакана нет: «не считали» и
          «модель согласна с рынком» — разные утверждения. */}
      {edge !== "" && (
        <Qual
          w={W.edge}
          color={toneColor(edgeTone(fair.price, mid))}
          title={edgeTitle(mid, exec, fair.price_normal)}
        >
          ({edge})
        </Qual>
      )}
      {band !== null && (
        <Qual
          w={W.band}
          title={
            `порог тейкера: покупать дешевле ${band.buyBelow}, продавать дороже ` +
            `${band.sellAbove}. Комиссия ${band.perShare} на share; порог посчитан ` +
            `в точке справедливой цены, то есть приближённо`
          }
        >
          ±{band.perShare}
        </Qual>
      )}
    </>
  );
}

/**
 * Подсказка к преимуществу: три числа, и различать их обязательно.
 *
 * Показанное преимущество считается против СЕРЕДИНЫ стакана — это ответ на
 * вопрос «сколько исход недооценён». Тейкер при этом платит аск и сверху
 * комиссию, и его кромка меньше на половину спреда со ставкой; на пятиминутках
 * разница бывает больше самой кромки. Третье — цена по прежней модели: журналом
 * прогноза посчитаны две недели именно ею, и вчерашнюю строку сверять надо с
 * ней, а не с сегодняшним числом.
 */
function edgeTitle(mid: string | null, exec: string, normal: string | undefined): string {
  const parts = [`середина стакана ${mid ?? "—"}; модель минус середина — это и есть преимущество`];
  parts.push(
    exec === ""
      ? "по цене налива не посчитано: аска нет либо расписание комиссии незнакомо"
      : `тейкеру достанется ${exec}: аск и комиссия съедают половину спреда со ставкой`,
  );
  if (normal) {
    parts.push(`прежняя модель (нормаль, безусловный разброс) давала ${normal}`);
  }
  return parts.join(". ");
}

/**
 * Цвет исхода. Up зелёный, Down жёлтый — не красный.
 *
 * Красный в терминале означает ПРОДАЖУ и убыток, и красить им половину рынков
 * значило бы говорить «этот исход плохой», тогда как он всего лишь второй.
 */
function outcomeColor(outcome: string): string {
  return outcome.toLowerCase() === "up" ? "var(--color-buy)" : "var(--color-warn)";
}
